import { useContext, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { AppContext } from '@edx/frontend-platform/react';

import {
  GET_CART,
  INSERT_COURSE_TO_CART,
  INSERT_PROGRAM_TO_CART,
  UPDATE_CART_ITEM,
  DELETE_CART_ITEM,
} from '../queries/cart';
import { useOrgId } from './useOrgId';

const isCartStatus = (status) => status === 'draft' || status === 'pending';
const isWishlistStatus = (status) => status === 'wishlist';

export const useCart = () => {
  const { authenticatedUser } = useContext(AppContext);
  const userId = authenticatedUser?.id ?? authenticatedUser?.userId;
  const organisationId = useOrgId();

  const [pendingTarget, setPendingTarget] = useState(null);

  const {
    data, loading, error, refetch,
  } = useQuery(GET_CART, {
    skip: !userId,
  });

  const [insertCourseMutation] = useMutation(INSERT_COURSE_TO_CART);
  const [insertProgramMutation] = useMutation(INSERT_PROGRAM_TO_CART);
  const [updateCartMutation] = useMutation(UPDATE_CART_ITEM);
  const [deleteCartMutation] = useMutation(DELETE_CART_ITEM);

  const {
    coursesInCart,
    coursesInWishlist,
    cartItemByCourseId,
    programsInCart,
    programsInWishlist,
    cartItemByProgramId,
  } = useMemo(() => {
    const courseCart = new Set();
    const courseWishlist = new Set();
    const byCourseId = new Map();
    const programCart = new Set();
    const programWishlist = new Set();
    const byProgramId = new Map();
    (data?.cart || []).forEach((row) => {
      if (row.courseId) {
        byCourseId.set(row.courseId, row);
        if (isCartStatus(row.status)) {
          courseCart.add(row.courseId);
        }
        if (isWishlistStatus(row.status)) {
          courseWishlist.add(row.courseId);
        }
      }
      if (row.programId) {
        byProgramId.set(row.programId, row);
        if (isCartStatus(row.status)) {
          programCart.add(row.programId);
        }
        if (isWishlistStatus(row.status)) {
          programWishlist.add(row.programId);
        }
      }
    });
    return {
      coursesInCart: courseCart,
      coursesInWishlist: courseWishlist,
      cartItemByCourseId: byCourseId,
      programsInCart: programCart,
      programsInWishlist: programWishlist,
      cartItemByProgramId: byProgramId,
    };
  }, [data]);

  const canMutate = Boolean(userId && organisationId);

  /**
   * Toggle a course between (cart | wishlist | absent).
   * - target: 'cart' | 'wishlist'
   * - mode: required when target === 'cart', ignored otherwise (preserves existing mode)
   */
  const setCourseCartState = async (courseId, target, mode) => {
    if (!canMutate || !courseId) {
      return;
    }
    const existing = cartItemByCourseId.get(courseId);
    const existingIsCart = existing && isCartStatus(existing.status);
    const existingIsWishlist = existing && isWishlistStatus(existing.status);

    setPendingTarget(target);
    try {
      if (
        (target === 'cart' && existingIsCart)
        || (target === 'wishlist' && existingIsWishlist)
      ) {
        await deleteCartMutation({ variables: { keyId: existing.id } });
      } else if (existing) {
        await updateCartMutation({
          variables: {
            keyId: existing.id,
            status: target === 'cart' ? 'draft' : 'wishlist',
            mode: target === 'cart' ? (mode || existing.mode || 'honor') : (existing.mode || 'honor'),
          },
        });
      } else {
        await insertCourseMutation({
          variables: {
            courseId,
            userId,
            organisationId,
            status: target === 'cart' ? 'draft' : 'wishlist',
            mode: mode || 'honor',
          },
        });
      }
      await refetch();
    } finally {
      setPendingTarget(null);
    }
  };

  /**
   * Toggle a program between (cart | wishlist | absent). Programs have no mode.
   */
  const setProgramCartState = async (programId, target) => {
    if (!canMutate || !programId) {
      return;
    }
    const existing = cartItemByProgramId.get(programId);
    const existingIsCart = existing && isCartStatus(existing.status);
    const existingIsWishlist = existing && isWishlistStatus(existing.status);

    setPendingTarget(target);
    try {
      if (
        (target === 'cart' && existingIsCart)
        || (target === 'wishlist' && existingIsWishlist)
      ) {
        await deleteCartMutation({ variables: { keyId: existing.id } });
      } else if (existing) {
        await updateCartMutation({
          variables: {
            keyId: existing.id,
            status: target === 'cart' ? 'draft' : 'wishlist',
            mode: existing.mode || '',
          },
        });
      } else {
        await insertProgramMutation({
          variables: {
            programId,
            userId,
            organisationId,
            status: target === 'cart' ? 'draft' : 'wishlist',
          },
        });
      }
      await refetch();
    } finally {
      setPendingTarget(null);
    }
  };

  const updateCartMode = async (courseId, mode) => {
    const existing = cartItemByCourseId.get(courseId);
    if (!existing || !mode) {
      return;
    }
    await updateCartMutation({
      variables: { keyId: existing.id, status: existing.status, mode },
    });
    await refetch();
  };

  return {
    data,
    loading,
    error,
    refetch,
    coursesInCart,
    coursesInWishlist,
    cartItemByCourseId,
    programsInCart,
    programsInWishlist,
    cartItemByProgramId,
    setCourseCartState,
    setProgramCartState,
    updateCartMode,
    pendingTarget,
    canMutate,
  };
};
